/**
 * Reserve Telemetry Layer
 * 
 * Camada de telemetria desacoplada da UI.
 * Toda escrita no Reserve DEVE passar por Edge Functions.
 * Nunca expor chaves de service role no client.
 */

import type { LodgingItem } from './lodgingApi';

const RESERVE_SUPABASE_URL = import.meta.env.VITE_RESERVE_SUPABASE_URL;
const RESERVE_ANON_KEY = import.meta.env.VITE_RESERVE_SUPABASE_ANON_KEY;
const SITE_SLUG = import.meta.env.VITE_PORTAL_SITE_SLUG || 'urubici';
const CITY_CODE = import.meta.env.VITE_RESERVE_CITY_CODE || 'URB';

// Eventos suportados
export type TelemetryEvent =
  | 'reserve_home_view'
  | 'reserve_results_view'
  | 'reserve_filter_apply'
  | 'reserve_property_view'
  | 'reserve_cta_whatsapp_click'
  | 'reserve_cta_phone_click'
  | 'reserve_cta_website_click'
  | 'reserve_cta_map_click'
  | 'reserve_search_execute'
  | 'reserve_similar_click'
  // Phase 2: Booking flow signals
  | 'reserve_property_detail_view'
  | 'reserve_card_click'
  | 'reserve_unit_selected'
  | 'reserve_booking_initiated'
  | 'reserve_booking_flow_started'
  | 'reserve_booking_intent_created'
  // Phase 3: Payment signals
  | 'reserve_payment_started'
  | 'reserve_payment_succeeded'
  | 'reserve_reservation_finalized'
  // Phase 4: Central Reservation signals
  | 'reserve_central_support_click'
  | 'reserve_lead_intent_capture'
  | 'reserve_assisted_inquiry_start'
  | 'reserve_complex_request_signal'
  // Phase 4: Ads
  | 'ad_click'
  | 'ad_impression'
  // Phase 5: Performance
  | 'web_vitals'
  // Phase 6: Error handling
  | 'error_boundary';

// Contexto mínimo por evento
export interface TelemetryContext {
  site_id: string;
  city_code: string;
  property_id?: string;
  property_slug?: string;
  origin?: string;
  session_id?: string;
  referrer?: string;
}

// Payload do evento
interface TelemetryPayload {
  event: TelemetryEvent;
  timestamp: string;
  context: TelemetryContext;
  metadata?: Record<string, unknown>;
}

// Configuração
const TELEMETRY_CONFIG = {
  // Endpoint da Edge Function no Reserve
  endpoint: `${RESERVE_SUPABASE_URL}/functions/v1/track-event`,
  enabled: true, // Habilitado para envio real
  debug: import.meta.env.DEV,
  batchSize: 1, // Eventos individuais por enquanto
};

// Detect device type
const getDeviceType = (): string => {
  if (typeof window === 'undefined') return 'server';
  const width = window.innerWidth;
  if (width < 640) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

// Gera session_id persistente na sessão
export const getSessionId = (): string => {
  if (typeof window === 'undefined') return 'ssr';
  
  let sessionId = sessionStorage.getItem('reserve_session_id');
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('reserve_session_id', sessionId);
  }
  return sessionId;
};

// Contexto base para todos os eventos
const getBaseContext = (): TelemetryContext => ({
  site_id: SITE_SLUG,
  city_code: CITY_CODE,
  session_id: getSessionId(),
  referrer: typeof document !== 'undefined' ? document.referrer : undefined,
});

/**
 * Envia evento de telemetria
 * 
 * NOTA: Esta função está preparada para quando a Edge Function
 * `track-event` existir no Reserve. Atualmente loga em dev.
 */
export async function trackEvent(
  event: TelemetryEvent,
  metadata?: Record<string, unknown>,
  context?: Partial<TelemetryContext>
): Promise<void> {
  const payload: TelemetryPayload = {
    event,
    timestamp: new Date().toISOString(),
    context: {
      ...getBaseContext(),
      ...context,
    },
    metadata,
  };

  // Modo debug: loga no console
  if (TELEMETRY_CONFIG.debug) {
    console.log('[Telemetry]', payload);
  }

  // Se não habilitado (dev), não envia
  if (!TELEMETRY_CONFIG.enabled) {
    return;
  }

  // Se não temos config do Reserve, não envia (graceful degradation)
  if (!RESERVE_SUPABASE_URL || !RESERVE_ANON_KEY) {
    if (TELEMETRY_CONFIG.debug) {
      console.warn('[Telemetry] Reserve not configured, event not sent');
    }
    return;
  }

  try {
    // Envio via Edge Function
    const response = await fetch(TELEMETRY_CONFIG.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': RESERVE_ANON_KEY,
        'Authorization': `Bearer ${RESERVE_ANON_KEY}`,
      },
      body: JSON.stringify({
        event_name: payload.event,
        city_code: payload.context.city_code,
        session_id: payload.context.session_id,
        entity_type: 'property',
        entity_id: payload.context.property_id,
        metadata: {
          ...payload.metadata,
          origin: payload.context.origin,
          referrer: payload.context.referrer,
        },
        device_type: getDeviceType(),
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        referrer: typeof document !== 'undefined' ? document.referrer : undefined,
      }),
      keepalive: true, // Garante envio mesmo se usuário sair da página
    });
    
    if (!response.ok) {
      throw new Error(`Telemetry failed: ${response.status}`);
    }

    if (TELEMETRY_CONFIG.debug) {
      console.log('[Telemetry] Sent:', payload.event);
    }
  } catch (error) {
    // Telemetria nunca deve quebrar a experiência do usuário
    if (TELEMETRY_CONFIG.debug) {
        console.error('[Telemetry] Failed to send:', error);
    }
  }
}

/**
 * Hooks de conveniência para tracking de páginas
 */

// Track page view na home
export function trackHomeView(origin?: string): void {
  void trackEvent('reserve_home_view', undefined, { origin });
}

// Track page view na listagem
export function trackResultsView(
  filters?: Record<string, string>,
  resultCount?: number
): void {
  void trackEvent(
    'reserve_results_view',
    { filters, resultCount },
    { origin: window.location.pathname + window.location.search }
  );
}

// Track aplicação de filtro
export function trackFilterApply(
  filterType: string,
  filterValue: string
): void {
  void trackEvent('reserve_filter_apply', { filterType, filterValue });
}

// Track view de propriedade
export function trackPropertyView(
  property: LodgingItem,
  origin?: string
): void {
  void trackEvent(
    'reserve_property_view',
    {
      propertyType: property.lodgingTypeId,
      hasDirectContact: property.hasDirectContact,
    },
    {
      property_id: property.id,
      property_slug: property.slug,
      origin,
    }
  );
}

// Track clicks em CTAs
export function trackCTAClick(
  type: 'whatsapp' | 'phone' | 'website' | 'map',
  property: LodgingItem
): void {
  const eventMap: Record<typeof type, TelemetryEvent> = {
    whatsapp: 'reserve_cta_whatsapp_click',
    phone: 'reserve_cta_phone_click',
    website: 'reserve_cta_website_click',
    map: 'reserve_cta_map_click',
  };

  void trackEvent(
    eventMap[type],
    { ctaType: type },
    {
      property_id: property.id,
      property_slug: property.slug,
    }
  );
}

// Track busca executada
export function trackSearchExecute(
  query: string,
  resultCount: number
): void {
  void trackEvent(
    'reserve_search_execute',
    { query, resultCount }
  );
}

// Track click em similar
export function trackSimilarClick(
  fromProperty: LodgingItem,
  toProperty: LodgingItem
): void {
  void trackEvent(
    'reserve_similar_click',
    {
      fromSlug: fromProperty.slug,
      toSlug: toProperty.slug,
    },
    {
      property_id: fromProperty.id,
    }
  );
}

// ============ Phase 2: Booking Flow Signals ============

// Track view de propriedade no fluxo de reserva (Reserve Connect)
export function trackPropertyDetailView(
  propertySlug: string,
  propertyId?: string,
  origin?: string
): void {
  void trackEvent(
    'reserve_property_detail_view',
    { origin },
    {
      property_id: propertyId,
      property_slug: propertySlug,
    }
  );
}

// Track click em card de propriedade (vindo da busca)
export function trackCardClick(
  propertySlug: string,
  propertyId?: string,
  position?: number
): void {
  void trackEvent(
    'reserve_card_click',
    { position },
    {
      property_id: propertyId,
      property_slug: propertySlug,
    }
  );
}

// Track seleção de unidade
export function trackUnitSelected(
  propertySlug: string,
  unitId: string,
  unitName: string,
  price?: number
): void {
  void trackEvent(
    'reserve_unit_selected',
    { unitId, unitName, price },
    {
      property_slug: propertySlug,
    }
  );
}

// Track início de reserva (CTA "Reservar agora")
export function trackBookingInitiated(
  propertySlug: string,
  unitId: string,
  checkIn: string,
  checkOut: string,
  guests: number
): void {
  void trackEvent(
    'reserve_booking_initiated',
    { unitId, checkIn, checkOut, guests },
    {
      property_slug: propertySlug,
    }
  );
}

// Track início do fluxo de booking (/book/:slug page view)
export function trackBookingFlowStarted(
  propertySlug: string,
  unitId: string,
  checkIn: string,
  checkOut: string,
  guests: number
): void {
  void trackEvent(
    'reserve_booking_flow_started',
    { unitId, checkIn, checkOut, guests },
    {
      property_slug: propertySlug,
    }
  );
}

// Track criação de booking intent
export function trackBookingIntentCreated(
  propertySlug: string,
  intentId: string,
  totalAmount: number
): void {
  void trackEvent(
    'reserve_booking_intent_created',
    { intentId, totalAmount },
    {
      property_slug: propertySlug,
    }
  );
}

// Track início do pagamento
export function trackPaymentStarted(
  propertySlug: string,
  intentId: string,
  amount: number
): void {
  void trackEvent(
    'reserve_payment_started',
    { intentId, amount },
    {
      property_slug: propertySlug,
    }
  );
}

// Track pagamento aprovado
export function trackPaymentSucceeded(
  propertySlug: string,
  intentId: string,
  reservationId?: string
): void {
  void trackEvent(
    'reserve_payment_succeeded',
    { intentId, reservationId },
    {
      property_slug: propertySlug,
    }
  );
}

// Track reserva finalizada
export function trackReservationFinalized(
  propertySlug: string,
  reservationId: string,
  confirmationCode: string
): void {
  void trackEvent(
    'reserve_reservation_finalized',
    { reservationId, confirmationCode },
    {
      property_slug: propertySlug,
    }
  );
}

// ============ Phase 4: Central Reservation Signals ============

// Track click em CTA da Central de Reservas
export function trackCentralSupportClick(
  origin: 'header' | 'footer' | 'sticky_mobile' | 'property_page' | 'empty_results' | 'listing_page' | 'faq_section',
  context?: string
): void {
  void trackEvent(
    'reserve_central_support_click',
    { origin, context }
  );
}

// Track captura de intenção de lead
export function trackLeadIntentCapture(
  leadData: {
    propertyId?: string;
    propertySlug?: string;
    checkIn?: string;
    checkOut?: string;
    guests?: number;
    notes?: string;
  }
): void {
  void trackEvent(
    'reserve_lead_intent_capture',
    leadData,
    {
      property_id: leadData.propertyId,
      property_slug: leadData.propertySlug,
    }
  );
}

// Track início de consulta assistida
export function trackAssistedInquiryStart(
  property?: LodgingItem,
  urgency?: 'low' | 'medium' | 'high'
): void {
  void trackEvent(
    'reserve_assisted_inquiry_start',
    { urgency },
    property ? {
      property_id: property.id,
      property_slug: property.slug,
    } : undefined
  );
}

// Track sinal de solicitação complexa (quando usuário parece perdido)
export function trackComplexRequestSignal(
  signal: 'multiple_properties' | 'specific_dates' | 'group_size' | 'special_needs',
  context?: Record<string, unknown>
): void {
  void trackEvent(
    'reserve_complex_request_signal',
    { signal, context }
  );
}

/**
 * Hook React para tracking automático de page views
 * 
 * Uso: usePageTracking('reserve_results_view', { resultCount: 15 })
 */
import { useEffect } from 'react';

export function usePageTracking(
  event: TelemetryEvent,
  metadata?: Record<string, unknown>,
  context?: Partial<TelemetryContext>
): void {
  useEffect(() => {
    void trackEvent(event, metadata, context);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Executa apenas no mount
}
